import { apiFetch } from "../../lib/api";
import { useEffect, useMemo, useRef, useState } from "react";
import { postIpc } from "../../lib/ipc";
import { useLayoutSurfaceReport } from "../../lib/layoutTooling";
import { CodexTerminalFrame } from "../ui/CodexTerminalFrame";

type ProjectItem = {
  name: string;
  path: string;
  description: string;
  icon_key: string;
  primary_label: string;
  active: boolean;
  selected: boolean;
};

type ProjectSummaryCard = {
  id: string;
  title: string;
  subtitle: string;
  body: string;
  tags: string[];
  svg_markup: string;
};

type ProjectSummaryView = {
  project_path: string;
  prompt: string;
  style_prompt: string;
  style_label: string;
  mode: string;
  headline: string;
  blurb: string;
  updated_at: string;
  cards: ProjectSummaryCard[];
};

type ProjectManifestLink = {
  id: string;
  title: string;
  summary: string;
  target_kind: string;
  target: string;
  icon_key: string;
};

type ProjectManifestView = {
  source: string;
  home_canvas_id: string;
  home_title: string;
  links: ProjectManifestLink[];
  warnings: string[];
};

type ProjectShellLink = {
  id: string;
  title: string;
  kind: string;
  canvas_id: string;
  built_in_route: string;
  fallback_route_id: string;
  label: string;
  valid: boolean;
};

type ProjectShellView = {
  home_canvas_id: string;
  links: ProjectShellLink[];
  warnings?: string[];
};

type ProjectPayload = {
  managed_root: string;
  items: ProjectItem[];
  selected_path: string;
  selected_name: string;
  selected_description: string;
  selected_icon_key: string;
  selected_primary_label: string;
  active_path: string;
  shell: ProjectShellView;
  manifest?: ProjectManifestView | null;
  summary?: ProjectSummaryView | null;
};

const PROJECT_PATTERNS: Record<string, string[]> = {
  workspace: ["001100", "011110", "111111", "011110", "001100"],
  docs: ["111100", "100100", "111100", "100100", "111100"],
  rust: ["101110", "111010", "101110", "111000", "101000"],
  python: ["111100", "001110", "111100", "111000", "001110"],
  node: ["011110", "110011", "110011", "110011", "011110"],
  go: ["011110", "110000", "110111", "110011", "011110"],
  swift: ["110011", "011110", "001100", "011110", "110011"],
  workflow: ["100001", "010010", "001100", "010010", "100001"],
  plugin: ["111000", "101000", "111100", "001100", "001100"],
};

function ProjectSprite({ active, iconKey }: { active: boolean; iconKey: string }) {
  const pattern = PROJECT_PATTERNS[iconKey] ?? PROJECT_PATTERNS.workspace;
  return (
    <span className={`project-sprite ${active ? "is-active" : ""}`} aria-hidden="true">
      {pattern.join("").split("").map((cell, index) => (
        <span key={index} className={`project-sprite__pixel ${cell === "1" ? "is-on" : "is-off"}`} />
      ))}
    </span>
  );
}

export function ProjectsWorkbench({ payload }: { payload: unknown }) {
  const [apiProjects, setApiProjects] = useState<ProjectPayload | null>(null);

  // In web mode, payload may be null — fetch from API
  useEffect(() => {
    if (!payload) {
      apiFetch("/api/projects")
        .then((r) => r.json())
        .then((d) => {
          const items: ProjectItem[] = (d.projects || []).map((p: any, i: number) => ({
            name: p.name,
            path: p.path,
            description: "",
            icon_key: "rust",
            primary_label: p.name,
            active: i === 0,
            selected: i === 0,
          }));
          setApiProjects({
            managed_root: d.root || "",
            items,
            selected_path: items[0]?.path || "",
            selected_name: items[0]?.name || "",
            selected_description: "",
            selected_icon_key: "rust",
            selected_primary_label: items[0]?.name || "",
            active_path: items[0]?.path || "",
            shell: { home_canvas_id: "", links: [] },
          });
        })
        .catch(() => {});
    }
  }, [payload]);

  const [prompt, setPrompt] = useState("");
  const [stylePrompt, setStylePrompt] = useState("");
  const rootRef = useRef<HTMLElement | null>(null);

  const view = (payload || apiProjects) as ProjectPayload;
  if (!view) {
    return <div style={{ padding: "2rem", opacity: .4, color: "#6272a4", fontFamily: "'JetBrains Mono', monospace" }}>Loading projects...</div>;
  }
  const selected = view.items.find((item) => item.selected) ?? view.items[0];

  useEffect(() => {
    setPrompt("");
    setStylePrompt("");
  }, [selected?.path]);

  const summary = view.summary && view.summary.project_path === selected?.path ? view.summary : null;
  const manifestLinks = view.manifest?.links ?? [];
  const activeItem = view.items.find((item) => item.active) ?? null;

  const cards = useMemo(() => (summary?.cards ?? []).slice(0, 4), [summary]);
  const routes = useMemo(
    () =>
      (view.shell?.links ?? []).map((link) => ({
        id: link.id,
        title: link.title,
        kind: link.kind,
        canvas_id: link.canvas_id,
        built_in_route: link.built_in_route,
        valid: link.valid,
        fallback_route_id: link.fallback_route_id,
      })),
    [view.shell]
  );
  const edges = useMemo(() => {
    const links = view.shell?.links ?? [];
    const root = links.find((link) => link.id === "project-home");
    if (!root) return [];
    return links
      .filter((link) => link.id !== root.id)
      .map((link) => ({
        id: `${root.id}-${link.id}`,
        from_route_id: root.id,
        to_route_id: link.id,
        label: link.label,
        kind: "nav",
        visible: true,
      }));
  }, [view.shell]);

  useLayoutSurfaceReport({
    route: "projects",
    surfaceId: "projects-shell",
    rootRef,
    selection: {
      projectId: selected?.path,
      canvasId: selected?.path ? `${selected.path}::project-home` : "",
    },
    routes,
    edges,
    deps: [selected?.path, summary?.updated_at ?? "", cards.length, routes.length, edges.length],
  });

  const requestSummary = (auto: boolean) => {
    if (!selected) return;
    postIpc({
      kind: "project-summary-generate",
      data: JSON.stringify({
        path: selected.path,
        prompt: auto ? "" : prompt,
        style_prompt: stylePrompt,
        auto,
      }),
    });
  };

  const selectedName = selected?.name ?? view.selected_name;
  const selectedDescription = selected?.description ?? view.selected_description;
  const selectedPath = selected?.path ?? view.selected_path;
  const selectedPrimaryLabel = selected?.primary_label ?? view.selected_primary_label;
  const selectedIconKey = selected?.icon_key ?? view.selected_icon_key;
  const summaryPrompt = summary
    ? `${summary.mode === "auto" ? "summary ready" : "prompted summary"} · ${summary.style_label} · ${summary.updated_at}`
    : `generate project canvas for ${selectedName}`;

  return (
    <main className="ui-shell projects-shell" ref={rootRef}>
      <section className="projects-workbench" data-layout-node="canvas" data-layout-id="projects.canvas" data-layout-kind="canvas" data-layout-title="projects">
        <aside className="projects-browser" data-layout-node="slab" data-layout-id="projects.browser" data-layout-kind="slab" data-layout-title="project list" data-grid-column="1" data-grid-row="1" data-grid-width="3" data-grid-height="12" data-cap-internal-scroll="true">
          <div className="projects-browser__header">
            <strong>Projects</strong>
            <em>{view.items.length} managed workspaces</em>
          </div>

          <div className="projects-browser__list" data-layout-node="block" data-layout-id="projects.browser.list" data-layout-kind="block" data-layout-title="project rows" data-layout-parent="projects.browser" data-cap-internal-scroll="true">
            {view.items.map((item, index) => (
              <button
                key={item.path}
                type="button"
                className={`project-row ${item.selected ? "is-selected" : ""} ${item.active ? "is-active" : ""}`}
                onClick={() => postIpc({ kind: "select-project", data: item.path })}
                onDoubleClick={() => postIpc({ kind: "set-active-project", data: item.path })}
              >
                <span className="project-row__index">{String(index + 1).padStart(2, "0")}</span>
                <ProjectSprite active={item.active} iconKey={item.icon_key} />
                <span className="project-row__body">
                  <strong>{item.name}</strong>
                  <em>{item.description}</em>
                </span>
                {item.active ? <span className="project-row__tag">active</span> : null}
              </button>
            ))}
          </div>

          <div className="projects-browser__actions">
            <button type="button" onClick={() => postIpc({ kind: "section-create-project" })}>
              new project
            </button>
          </div>
        </aside>

        <section className="projects-note" data-layout-node="slab" data-layout-id="projects.note" data-layout-kind="slab" data-layout-title="project canvas" data-grid-column="4" data-grid-row="1" data-grid-width="9" data-grid-height="12">
          <div className="projects-note__topline">
            <div className="projects-note__identity">
              <span className="projects-note__label">project canvas</span>
              <strong>{selectedName}</strong>
              <em>{selectedPrimaryLabel}</em>
            </div>
            <div className="projects-note__meta">
              <span className="projects-note__meta-chip is-active">
                {activeItem ? `active ${activeItem.name}` : "no active workspace"}
              </span>
              <span className={`projects-note__meta-chip ${summary ? "is-ready" : ""}`}>
                {summary ? `${summary.mode} summary` : "summary idle"}
              </span>
            </div>
          </div>

          <div className="projects-note__path">{selectedPath}</div>

          <div className="projects-note__controls" data-layout-node="block" data-layout-id="projects.controls" data-layout-kind="block" data-layout-title="summary controls" data-layout-parent="projects.note" data-cap-wrap="true">
            <input
              type="text"
              value={prompt}
              placeholder="Prompt a project page idea or leave blank for auto."
              onChange={(event) => setPrompt(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  requestSummary(false);
                }
              }}
            />
            <input
              type="text"
              value={stylePrompt}
              className="projects-note__style-input"
              placeholder="Style: grafana inspired, steampunk, space, bold neon lines..."
              onChange={(event) => setStylePrompt(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  requestSummary(false);
                }
              }}
            />
            <button type="button" onClick={() => requestSummary(true)}>
              auto summarize
            </button>
            <button type="button" onClick={() => requestSummary(false)}>
              generate
            </button>
            <button
              type="button"
              className="is-primary"
              onClick={() => postIpc({ kind: "open-project-chat", data: selectedPath })}
            >
              open chat
            </button>
          </div>

          {manifestLinks.length ? (
            <div
              className="projects-note__linkbar"
              data-layout-node="block"
              data-layout-id="projects.links"
              data-layout-kind="block"
              data-layout-title="project routes"
              data-layout-parent="projects.note"
              data-cap-wrap="true"
            >
              {manifestLinks.map((link) => (
                <button
                  key={link.id}
                  type="button"
                  className="projects-note__link"
                  onClick={() =>
                    postIpc({
                      kind: "project-open-link",
                      data: JSON.stringify({
                        path: selectedPath,
                        target_kind: link.target_kind,
                        target: link.target,
                      }),
                    })
                  }
                >
                  <ProjectSprite active={false} iconKey={link.icon_key} />
                  <span>
                    <strong>{link.title}</strong>
                    <em>{link.summary}</em>
                  </span>
                </button>
              ))}
            </div>
          ) : null}

          {view.shell?.warnings?.length ? (
            <div className="projects-note__warnings">
              {view.shell.warnings.slice(0, 2).map((warning) => (
                <span key={warning}>{warning}</span>
              ))}
            </div>
          ) : null}

          <div
            className="projects-note__canvas"
            data-layout-node="block"
            data-layout-id={summary ? "projects.summary" : "projects.blank-canvas"}
            data-layout-kind="block"
            data-layout-title={summary ? "generated project summary" : "blank project canvas"}
            data-layout-parent="projects.note"
            data-cap-remix={summary ? "true" : undefined}
            data-cap-save={summary ? "true" : undefined}
          >
            <CodexTerminalFrame title="project canvas" prompt={summaryPrompt} className="projects-note__frame" compact>
              {summary ? (
                <div className="project-summary">
                  <header className="project-summary__hero">
                    <span className="project-summary__eyebrow">
                      {summary.mode === "auto" ? "auto summary" : "prompted layout"}
                    </span>
                    <h2>{summary.headline}</h2>
                    <span className="project-summary__updated">
                      {summary.style_label} · {summary.updated_at}
                    </span>
                  </header>

                  <div className="project-summary__grid">
                    {cards.map((card) => (
                      <article key={card.id} className={`project-summary-card project-summary-card--${card.id}`} data-layout-node="block" data-layout-id={`projects.card.${card.id}`} data-layout-kind="block" data-layout-title={card.title} data-layout-parent="projects.summary" data-cap-remix="true" data-cap-stage="true">
                        <header className="project-summary-card__header">
                          <div>
                            <span>{card.subtitle}</span>
                            <strong>{card.title}</strong>
                          </div>
                          <div className="project-summary-card__tags">
                            {card.tags.slice(0, 2).map((tag) => (
                              <em key={tag}>{tag}</em>
                            ))}
                          </div>
                        </header>
                        <div
                          className="project-summary-card__visual"
                          dangerouslySetInnerHTML={{ __html: card.svg_markup }}
                        />
                        <p>{card.body}</p>
                      </article>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="projects-note__empty">
                  <div className="projects-note__canvas-mark" aria-hidden="true">
                    <ProjectSprite active iconKey={selectedIconKey} />
                  </div>
                </div>
              )}
            </CodexTerminalFrame>
          </div>
        </section>
      </section>
    </main>
  );
}
